import React from "react";
import {Button, Checkbox, Drawer} from "antd";
import {Translate} from "react-localize-redux";
import {connect} from "react-redux";
import {Nullable} from "@data-force/app";
import {CheckboxChangeEvent} from "antd/es/checkbox";
import {CheckboxChangeEventTarget} from "antd/es/checkbox/Checkbox";
import StoreState from "../../interfaces/store/storeState";
import "./FilterDrawer.scss";
import {getLocalePrefix, setFocusedElement} from "../../utils/common";
import {ContentType, JobTag, TestTag} from "../../enums/enums";
import {JobRequestParams, SelectedFiltersData} from "../../interfaces/store/actions/payload/job";
import {GetJobListAction, SetSelectedFiltersAction, SetFiltersDrawerVisibilityAction} from "../../interfaces/store/actions/job";
import {getJobList, setSelectedFilters, setFiltersDrawerVisibility} from "../../store/actions/job";

const {
    COMPLETED, REJECTED, EXPIRED, APPROVED_FOR_PAYMENT,
}: typeof JobTag = JobTag;

const {
    PENDING, PASS, FAILED,
}: typeof TestTag = TestTag;

interface State {
    skills: string[]
    types: string[]
    statuses: string[]
    decoratedStatuses: string[]
}

interface StateProps {
    visible: boolean
    isTest: Nullable<boolean>
    isMobile: boolean
    activeJobType: string
    activeTestType: string
    filter: string
    searchValue: string
}

interface DispatchProps {
    setFiltersDrawerVisibility(payload: boolean): SetFiltersDrawerVisibilityAction

    getJobList(payload?: JobRequestParams): GetJobListAction

    setSelectedFilters(payload: SelectedFiltersData): SetSelectedFiltersAction
}

type Props = StateProps & DispatchProps;

class FilterDrawer extends React.Component<Props, State> {
    private static readonly translatePath: string = "components.FilterDrawer.FilterDrawer";

    private static readonly skills = ["LANGUAGE", "TRANSCRIPTION", "ANNOTATION"];

    private static readonly types = ["QUALIFICATIONS", "PRACTICE"];

    private static readonly statuses = [PENDING, PASS, FAILED, EXPIRED];

    private static readonly decoratedStatuses: string[] = [COMPLETED, REJECTED, EXPIRED, APPROVED_FOR_PAYMENT];

    state: State = {
        skills: [],
        types: [],
        statuses: [],
        decoratedStatuses: [],
    };

    constructor(props: Props) {
        super(props);
        this.onCloseHandler = this.onCloseHandler.bind(this);
        this.preventContextMenu = this.preventContextMenu.bind(this);
        this.applyFiltersHandler = this.applyFiltersHandler.bind(this);
    }

    /**
     * Closing Filter Drawer and restoring focus to last focused list item
     */
    onCloseHandler(): void {
        this.resetFilters();
        this.props.setFiltersDrawerVisibility(false);
        setFocusedElement();
    }

    /**
     * On filter value change, we updated state with current data
     * @param {string} filter
     * @param {string} filterKey
     * @param {CheckboxChangeEvent} e
     */
    onFilterChange(filter: string, filterKey: string, e: CheckboxChangeEvent): void {
        const {checked}: CheckboxChangeEventTarget = e.target;
        if (checked) {
            this.setState((prevState: State): State => ({
                ...prevState,
                [filterKey]: [...prevState[filterKey as keyof State], filter],
            }));
        } else {
            this.setState((prevState: State): State => ({
                ...prevState,
                [filterKey]: prevState[filterKey as keyof State].filter((item: string): boolean => item !== filter),
            }));
        }
    }

    /**
     * Return filter section with checkbox list for a given filter group
     * @param {string[]} filters
     * @param {string} filterKey
     * @return {React.ReactNode}
     */
    getFilterSection(filters: string[], filterKey: string): React.ReactNode {
        return (
            <div className="df-filter-section">
                <strong><Translate id={`filters.${filterKey}.text`}/></strong>
                {filters.map((filter: string): React.ReactNode => (
                    <Checkbox
                        key={`${filter}_${filterKey}`}
                        onChange={this.onFilterChange.bind(this, filter, filterKey)}
                    >
                        <Translate id={`filters.${filter}.text`}/>
                    </Checkbox>
                ))}
            </div>
        );
    }

    /**
     * Get filter sections for test type list items
     * @param {string} activeTestType
     * @return {React.ReactNode}
     */
    getTestFilters(activeTestType: string): React.ReactNode {
        const {
            skills, statuses, types,
        }: typeof FilterDrawer = FilterDrawer;
        return (
            <>
                {this.getFilterSection(skills, "skills")}
                {this.getFilterSection(types, "types")}
                {activeTestType === ContentType.COMPLETED && this.getFilterSection(statuses, "statuses")}
            </>
        );
    }

    /**
     * Generates a filter section for regular jobs
     */
    getRegularFilters(): React.ReactNode {
        const {decoratedStatuses}: typeof FilterDrawer = FilterDrawer;
        return this.getFilterSection(decoratedStatuses, "decoratedStatuses");
    }

    /**
     * Apply filters button handler,
     * collects state filter data and sends an action handling applying filters
     */
    applyFiltersHandler(): void {
        const {isTest, filter, searchValue}: Props = this.props;
        const {
            skills, types, statuses, decoratedStatuses,
        }: State = this.state;
        this.props.getJobList({
            filter, title: searchValue, isTest, skills, types, statuses, decoratedStatuses,
        });
        this.props.setSelectedFilters({
            skills, types, statuses, decoratedStatuses,
        });
        this.resetFilters();
        this.onCloseHandler();
    }

    /**
     * Prevent context menu opening on long press only on mobile
     * @param {React.MouseEvent} e
     */
    preventContextMenu(e: React.MouseEvent): void {
        if (this.props.isMobile) {
            e.preventDefault();
        }
    }

    /**
     *  Resets state filter data
     */
    resetFilters(): void {
        this.setState({
            skills: [],
            types: [],
            statuses: [],
            decoratedStatuses: [],
        });
    }

    render(): React.ReactNode {
        const {
            visible, isTest, activeJobType, activeTestType,
        }: Props = this.props;
        const {translatePath}: typeof FilterDrawer = FilterDrawer;
        const localePrefix: string = getLocalePrefix(isTest);
        const type: string = isTest ? activeTestType : activeJobType;
        return (
            <div onContextMenu={this.preventContextMenu}>
                <Drawer
                    className="df-filter-drawer"
                    title={<Translate id={`${translatePath}.${type}_${localePrefix}Title.text`}/>}
                    visible={visible}
                    destroyOnClose
                    onClose={this.onCloseHandler}
                    maskClosable
                    footer={[
                        <Button
                            key="cancel"
                            onClick={this.onCloseHandler}
                        >
                            <Translate id={`${translatePath}.cancelBtn.text`}/>
                        </Button>,
                        <Button
                            key="apply"
                            type="primary"
                            onClick={this.applyFiltersHandler}
                        >
                            <Translate id={`${translatePath}.applyBtn.text`}/>
                        </Button>,
                    ]}
                >
                    <div className="df-filter-drawer-intro">
                        <Translate id={`${translatePath}.selectFilterLabel.text`}/>
                    </div>
                    {isTest ? this.getTestFilters(activeTestType) : this.getRegularFilters()}
                </Drawer>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        visible: state.job.isFiltersDrawerVisible,
        isTest: state.job.isTest,
        isMobile: state.mobile.isMobile,
        activeJobType: state.job.activeJobType,
        activeTestType: state.test.activeTestType,
        filter: state.job.filter,
        searchValue: state.job.searchValue || "",
    };
}

const mapDispatchToProps: DispatchProps = {
    setFiltersDrawerVisibility,
    getJobList,
    setSelectedFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterDrawer);
